import React, { useContext, useEffect, useMemo, useRef } from "react";
import { Configure, InstantSearch } from "react-instantsearch-dom";

import { ROWS_PER_PAGE_ON_PAGINATED_TABLE } from "lookup";
import { JobsContext, MatchContext, SearchContext } from "context/providers";

import ResultsContainer from "./Results";
import {
  abortCurrentStreamSearch,
  getCustomSearchClient,
  getFilteredUserIDs,
  getSearchConfigureParams,
} from "utils/helpers/search";

const SearchContainer = ({ children }) => {
  const {
    searchState,
    setSearchState,
    hideSkipped,
    hideRejected,
    hideMatchedAndApplied,
    operators,
    reRankHitsForJob,
    handleReadyForSearch,
    handleReRankHitsForJob,
    isReadyForSearch,
    pageIsLoaded,
    handlePageIsLoaded,
  } = useContext(SearchContext);
  const { matches } = useContext(MatchContext);
  const { jobOpp } = useContext(JobsContext);

  const matchesTriggeredRef = useRef(null);

  const platformUsersIndexName = process.env.REACT_APP_OS_USERS_INDEX_NAME;

  const searchConfigureParams = useMemo(
    () =>
      getSearchConfigureParams(searchState, operators, platformUsersIndexName),
    [operators, platformUsersIndexName, searchState]
  );

  const searchMetaData =
    jobOpp?.id && reRankHitsForJob?.id
      ? { reRankHitsForJob: reRankHitsForJob?.id }
      : undefined;

  useEffect(() => {
    return () => {
      abortCurrentStreamSearch();
    };
  }, []);

  // sync facetsExcludes filters with no api request
  useEffect(() => {
    if (matchesTriggeredRef.current === null) {
      matchesTriggeredRef.current = true;
      return;
    }

    if (!pageIsLoaded || searchState.basicSearch) {
      return;
    }

    const filteredObjectIDs = getFilteredUserIDs(
      matches,
      hideSkipped,
      hideRejected,
      hideMatchedAndApplied
    );

    const finalState = {
      ...searchState,
      configProps: {
        ...searchState.configProps,
        facetsExcludes: {
          objectID: filteredObjectIDs,
        },
      },
    };

    handleReadyForSearch(null);
    setSearchState(finalState, true);
    handleReadyForSearch(null);

    setTimeout(() => {
      handleReadyForSearch(false);
    }, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  return (
    <InstantSearch
      searchClient={getCustomSearchClient(
        searchMetaData,
        {
          isReadyForSearch,
          pageIsLoaded,
          isLoadingReRanking: reRankHitsForJob?.isLoading,
          stream: true,
        },
        {
          handleReadyForSearch,
          handleReRankHitsForJob,
          handlePageIsLoaded,
          platformUsersIndexName,
        }
      )}
      indexName={platformUsersIndexName}
      onSearchStateChange={setSearchState}
      searchState={searchState}
    >
      <ResultsContainer reRankHitsForJob={reRankHitsForJob}>
        {children}
      </ResultsContainer>

      <Configure
        facets={["objectID"]}
        hitsPerPage={ROWS_PER_PAGE_ON_PAGINATED_TABLE}
        {...searchConfigureParams}
      />
    </InstantSearch>
  );
};

export default SearchContainer;
